import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Badge } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";

// Phase 4B, §12/§28. The browser never selects an arbitrary policy — only a
// requested transition (optional) and a reason. The policy itself is always
// resolved server-side; this dialog shows the RESULT (resolved policy +
// stage sequence) only after a successful submission, never as a client-
// side prediction it could get wrong or that could be mistaken for
// authorization.
export default function SubmitTaskApprovalDialog({ show, onHide, taskId, onSubmitted }) {
  const [transitions, setTransitions] = useState([]);
  const [requestedTransitionId, setRequestedTransitionId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!show) return;
    setRequestedTransitionId("");
    setReason("");
    setResult(null);
    api.get(`/tasks/${taskId}/allowed-transitions`).then((res) => setTransitions(Array.isArray(res.data?.data) ? res.data.data : [])).catch(() => setTransitions([]));
  }, [show, taskId]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/tasks/${taskId}/approval-requests`, {
        requested_transition_id: requestedTransitionId || undefined,
        reason: reason || undefined,
      });
      setResult(res.data?.data);
      toast.success("Approval request created.");
      onSubmitted?.();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_APPROVAL_NOT_REQUIRED") toast.error("No approval policy applies to this task.");
      else if (data?.code === "TASK_APPROVAL_REQUEST_EXISTS") toast.error("An approval request is already pending for this task.");
      else toast.error(data?.message || "Failed to submit for approval");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Submit for Approval</Modal.Title></Modal.Header>
      <Modal.Body>
        {!result ? (
          <>
            <Form.Label className="small">Requested Transition (optional)</Form.Label>
            <Form.Select value={requestedTransitionId} onChange={(e) => setRequestedTransitionId(e.target.value)}>
              <option value="">None — general approval only</option>
              {transitions.map((t) => <option key={t.transition_id} value={t.transition_id}>{t.name} → {t.to_status.name}</option>)}
            </Form.Select>
            <Form.Label className="small mt-3">Reason (optional)</Form.Label>
            <Form.Control as="textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </>
        ) : (
          <>
            <Alert variant="success" className="small">
              Approval request created. The task status will change after the required approval is completed.
            </Alert>
            <div className="small mb-2"><strong>Policy:</strong> {result.policy_name}</div>
            <div className="small mb-2"><strong>Expected approval sequence:</strong></div>
            <div className="d-flex flex-wrap gap-2">
              {(result.stages || []).map((s) => <Badge key={s.id} bg="light" text="dark">{s.stage_order}. {s.stage_name}</Badge>)}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result ? (
          <>
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button variant="primary" disabled={saving} onClick={submit}>{saving ? "Submitting..." : "Submit for Approval"}</Button>
          </>
        ) : (
          <Button variant="primary" onClick={onHide}>Close</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
